'use client';

import React, { useState } from 'react';
import { colors, baseStyles, formatNum } from './SharedUI';

// ==========================================
// TỪ ĐIỂN PATTERN TĨNH
// ==========================================
const PATTERN_CATEGORIES = [
  {
    id: 'bull_rev', title: '🟢 ĐẢO CHIỀU TĂNG (Tích Lũy)', color: colors.green,
    patterns: [
      { id: 'db_bot', name: 'Hai Đáy (Double Bottom)' },
      { id: 'ihs', name: 'Vai Đầu Vai Ngược' },
      { id: 'fall_wedge', name: 'Cái Nêm Giảm' }
    ]
  },
  {
    id: 'bear_rev', title: '🔴 ĐẢO CHIỀU GIẢM (Phân Phối)', color: colors.red,
    patterns: [
      { id: 'db_top', name: 'Hai Đỉnh (Double Top)' },
      { id: 'hs', name: 'Vai Đầu Vai (H&S)' },
      { id: 'rise_wedge', name: 'Cái Nêm Tăng' }
    ]
  },
  {
    id: 'continuation', title: '🔵 TIẾP DIỄN XU HƯỚNG', color: '#3b82f6',
    patterns: [
      { id: 'flag', name: 'Cờ Hiệu (Flag)' },
      { id: 'cup', name: 'Cốc & Tay Cầm' }
    ]
  },
  {
    id: 'neutral', title: '🟡 TRUNG TÍNH (Nén Giá)', color: colors.warn,
    patterns: [
      { id: 'sym_tri', name: 'Tam Giác Cân' },
      { id: 'box', name: 'Hộp Darvas (Rectangle)' }
    ]
  }
];

export default function PatternMatrixXRay({ symbol = 'BTCUSDT' }) {
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // Lưu array timeframe cho Pill
  const [detailedFrames, setDetailedFrames] = useState(null); // Lưu data chi tiết cho từng Card 1D, 4H, 1H
  const [aiSummary, setAiSummary] = useState(null);

  // ==========================================
  // THUẬT TOÁN NHẬN DIỆN PATTERN + BÓC TÁCH MỐC GIÁ
  // ==========================================
  const detectPatternLogic = (klines, timeframe) => {
    if (!klines || klines.length < 20) return null;

    const closes = klines.map(k => parseFloat(k[4]));
    const highs = klines.map(k => parseFloat(k[2]));
    const lows = klines.map(k => parseFloat(k[3]));
    const currentPrice = closes[closes.length - 1];

    const recentHighs = highs.slice(-30);
    const recentLows = lows.slice(-30);
    const maxHigh = Math.max(...recentHighs);
    const minLow = Math.min(...recentLows);
    const range = maxHigh - minLow;

    const recent5High = Math.max(...highs.slice(-5));
    const recent5Low = Math.min(...lows.slice(-5));
    const currentRange = recent5High - recent5Low;
    
    const isCompressing = currentRange < (range * 0.3); 
    const distanceToTop = maxHigh - currentPrice;
    const distanceToBot = currentPrice - minLow;

    // RULE 1: DOUBLE TOP (Hai Đỉnh)
    if (distanceToTop < range * 0.1 && !isCompressing) {
      return {
        id: 'db_top', name: 'Hai Đỉnh (Double Top)', color: colors.red,
        prices: [
          { label: 'Đỉnh kháng cự (Top)', val: maxHigh },
          { label: 'Viền cổ (Neckline)', val: minLow }
        ],
        reliability: '[⭐⭐⭐⭐] Cao (80%) - Chạm cản 2 lần không vỡ.',
        position: 'Đang phản ứng mạnh tại Đỉnh cũ, xuất hiện râu từ chối giá.',
        bias: '🔴 RỦI RO ĐẢO CHIỀU - Cẩn thận bị xả.'
      };
    }

    // RULE 2: DOUBLE BOTTOM (Hai Đáy)
    if (distanceToBot < range * 0.1 && !isCompressing) {
      return {
        id: 'db_bot', name: 'Hai Đáy (Double Bottom)', color: colors.green,
        prices: [
          { label: 'Đáy hỗ trợ (Bottom)', val: minLow },
          { label: 'Viền cổ (Neckline)', val: maxHigh }
        ],
        reliability: '[⭐⭐⭐⭐] Cao (80%) - Quét thanh khoản đáy thành công.',
        position: 'Đang rút râu tạo lực đỡ tại Đáy cũ.',
        bias: '🟢 TÍN HIỆU PHỤC HỒI - Phe mua nhập cuộc.'
      };
    }

    // RULE 3: CỐC & TAY CẦM (Cup & Handle)
    if (timeframe === '1D' && currentPrice > maxHigh * 0.95 && isCompressing) {
      return {
        id: 'cup', name: 'Cốc & Tay Cầm (Cup & Handle)', color: '#3b82f6',
        prices: [
          { label: 'Đáy Cốc (Cup Bottom)', val: minLow },
          { label: 'Hỗ trợ Tay Cầm (Handle Low)', val: recent5Low },
          { label: 'Miệng Cốc / Kháng cự', val: maxHigh }
        ],
        reliability: '[⭐⭐⭐⭐⭐] Rất Cao (90%) - Tích lũy vĩ mô.',
        position: 'Đang nén chặt ở phần Tay Cầm (Sát miệng cốc).',
        bias: '🔵 CỬA LONG CỰC SÁNG - Đợi nổ Vol là bay.'
      };
    }

    // RULE 4: TAM GIÁC CÂN / NÉN GIÁ (Symmetrical Triangle)
    if (isCompressing && distanceToTop > range * 0.2 && distanceToBot > range * 0.2) {
      return {
        id: 'sym_tri', name: 'Tam Giác Cân (Symmetrical Triangle)', color: colors.warn,
        prices: [
          { label: 'Cạnh trên (Downtrend line)', val: recent5High },
          { label: 'Cạnh dưới (Uptrend line)', val: recent5Low },
          { label: 'Đỉnh nén lớn nhất', val: maxHigh }
        ],
        reliability: '[⭐⭐⭐] Khá (70%) - Chờ xác nhận.',
        position: 'Giá kẹp sát ở phần chóp tam giác, biên độ dao động cạn kiệt.',
        bias: '🟡 CHỜ BREAKOUT - Nổ hướng nào đánh hướng đó.'
      };
    }

    // RULE 5: HỘP DARVAS (Sideway)
    if (!isCompressing && distanceToTop > range * 0.3 && distanceToBot > range * 0.3) {
      return {
        id: 'box', name: 'Hộp Darvas (Rectangle Channel)', color: colors.warn,
        prices: [
          { label: 'Cạnh trên Hộp (Resistance)', val: maxHigh },
          { label: 'Cạnh dưới Hộp (Support)', val: minLow }
        ],
        reliability: '[⭐⭐⭐] Trung bình - Market maker đang gom/xả tay bo.',
        position: 'Đang đi ngang vô định ở lưng chừng hộp.',
        bias: '🟡 ĐI NGANG (SIDEWAY) - Đứng ngoài hoặc đánh Ping-pong 2 đầu.'
      };
    }

    return null; // Không có cấu trúc
  };

  // ==========================================
  // HÀM QUÉT API
  // ==========================================
  const handleScan = async () => {
    if (!symbol) return;
    setIsScanning(true);
    setScanResult(null);
    setDetailedFrames(null);
    setAiSummary(null);

    try {
      const fetchKlines = (interval) => 
        fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${symbol.toUpperCase()}&interval=${interval}&limit=50`)
          .then(res => res.json());

      const [klines1D, klines4H, klines1H] = await Promise.all([
        fetchKlines('1d'), fetchKlines('4h'), fetchKlines('1h')
      ]);

      const res1D = detectPatternLogic(klines1D, '1D');
      const res4H = detectPatternLogic(klines4H, '4H');
      const res1H = detectPatternLogic(klines1H, '1H');

      // 1. Lọc data để hiển thị sáng đèn (Pills)
      let pillMap = {};
      const addPill = (patternData, tf) => {
        if (!patternData) return;
        if (!pillMap[patternData.id]) pillMap[patternData.id] = [];
        pillMap[patternData.id].push(tf);
      };
      addPill(res1D, '1D'); addPill(res4H, '4H'); addPill(res1H, '1H');
      setScanResult(pillMap);

      // 2. Lưu data chi tiết cho 3 Card Wireframe
      setDetailedFrames({ '1D': res1D, '4H': res4H, '1H': res1H });

      // 3. AI Tóm tắt
      let aiText = { macro: "1D không có mẫu hình định hướng.", micro: "1H chưa có tín hiệu kích hoạt rủi ro.", action: "Chưa có hợp lưu. Quan sát thêm." };
      
      if (res1D) aiText.macro = `Khung 1D đang hình thành [${res1D.name}]. Dòng tiền vĩ mô đang định hình cấu trúc này.`;
      if (res4H) aiText.macro += ` Khung 4H củng cố bằng [${res4H.name}].`;
      
      if (res1H && (res1H.id === 'db_top' || res1H.id === 'hs')) {
        aiText.micro = `Ngắn hạn 1H xuất hiện cấu trúc xả hàng [${res1H.name}].`;
        aiText.action = "Cảnh giác cao độ. Bật Mắt Thần soi phe Bán (Asks), chuẩn bị kịch bản Rũ hàng.";
      } else if (res1H && (res1H.id === 'db_bot' || res1H.id === 'ihs')) {
        aiText.micro = `Ngắn hạn 1H xuất hiện lực đỡ tạo [${res1H.name}].`;
        aiText.action = "Dấu hiệu gom hàng. Bật Mắt Thần soi lực Mua chủ động (CVD Dương) để canh LONG.";
      } else if (res1H && (res1H.id === 'sym_tri' || res1H.id === 'box')) {
        aiText.micro = `Ngắn hạn 1H đang đi ngang/nén trong [${res1H.name}].`;
        aiText.action = "Sắp có Breakout. Không đoán hướng, chờ gãy hẳn 1 biên rồi đánh theo xu hướng phá vỡ.";
      }
      setAiSummary(aiText);

    } catch (error) {
      console.error("Lỗi:", error);
      setAiSummary({ action: "🔴 Lỗi lấy dữ liệu từ Binance." });
    } finally {
      setIsScanning(false);
    }
  };

  const handleClear = () => {
    setScanResult(null); setDetailedFrames(null); setAiSummary(null);
  };

  const getDecimals = (val) => val < 1 ? 6 : 4;

  // ==========================================
  // RENDER CARD CHI TIẾT (1D, 4H, 1H)
  // ==========================================
  const renderDetailCard = (timeframe, label, data) => {
    if (!data) {
      return (
        <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: `1px dashed ${colors.border}`, padding: '16px', borderRadius: '8px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <strong style={{ fontSize: '13px', color: colors.textMain }}>{label}</strong>
          <div style={{ fontStyle: 'italic', fontSize: '12px' }}>⚪ Không quét ra cấu trúc chuẩn. Giá đang nhiễu hoặc đi theo xu hướng tự do.</div>
        </div>
      );
    }

    return (
      <div style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: `1px solid ${data.color}`, padding: '16px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <strong style={{ fontSize: '13px', color: data.color, borderBottom: `1px dashed ${data.color}50`, paddingBottom: '6px' }}>{label}</strong>
        
        <div style={{ fontSize: '12px', display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px' }}>
          <span style={{ color: colors.textSub }}>📐 Mẫu hình:</span>
          <strong style={{ color: '#fff' }}>{data.name}</strong>
        </div>

        {/* THÔNG SỐ MỐC GIÁ (LEVELS) */}
        <div style={{ fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.05)', padding: '8px', borderRadius: '4px', borderLeft: `2px solid ${data.color}` }}>
          <div style={{ color: colors.textSub, marginBottom: '4px', fontSize: '11px' }}>📍 Thông số Vùng Giá (Key Levels):</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {data.prices.map((p, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#e2e8f0' }}>- {p.label}:</span>
                <strong style={{ color: data.color }}>${formatNum(p.val, getDecimals(p.val))}</strong>
              </div>
            ))}
          </div>
        </div>

        <div style={{ fontSize: '12px', display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px' }}>
          <span style={{ color: colors.textSub }}>📊 Tin cậy:</span>
          <span style={{ color: '#e2e8f0' }}>{data.reliability}</span>
        </div>
        <div style={{ fontSize: '12px', display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px' }}>
          <span style={{ color: colors.textSub }}>📍 Vị trí:</span>
          <span style={{ color: '#e2e8f0' }}>{data.position}</span>
        </div>
        <div style={{ fontSize: '12px', display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px' }}>
          <span style={{ color: colors.textSub }}>🎯 Bias:</span>
          <strong style={{ color: data.color }}>{data.bias}</strong>
        </div>
      </div>
    );
  };

  return (
    <div style={{ ...baseStyles.card, marginBottom: '24px', border: `1px solid ${scanResult ? '#3b82f6' : colors.border}` }}>
      
      {/* HEADER & NÚT BẤM */}
      <div style={{ borderBottom: `1px solid ${colors.border}`, paddingBottom: '16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>📐</span>
          <strong style={{ color: colors.textMain, fontSize: '16px' }}>X-QUANG CẤU TRÚC ĐA KHUNG (PATTERN) | {symbol}</strong>
        </div>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          {scanResult && (
            <button onClick={handleClear} style={{ backgroundColor: 'transparent', color: colors.textSub, border: `1px solid ${colors.border}`, padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
              XÓA KẾT QUẢ
            </button>
          )}
          <button 
            onClick={handleScan} 
            disabled={isScanning}
            style={{ 
              backgroundColor: isScanning ? colors.warn : '#3b82f6', 
              color: isScanning ? '#000' : '#fff', 
              border: 'none', padding: '8px 20px', borderRadius: '6px', 
              cursor: isScanning ? 'not-allowed' : 'pointer', 
              fontSize: '13px', fontWeight: 'bold',
              boxShadow: isScanning ? 'none' : '0 0 10px rgba(59, 130, 246, 0.4)'
            }}
          >
            {isScanning ? '⏳ ĐANG TẢI & TOÁN NẾN...' : '🔍 QUÉT X-QUANG PATTERN'}
          </button>
        </div>
      </div>

      {/* DANH SÁCH PATTERN TĨNH / HIGHLIGHTS */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: detailedFrames ? '24px' : '0' }}>
        {PATTERN_CATEGORIES.map(category => (
          <div key={category.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: category.color }}>{category.title}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {category.patterns.map(p => {
                const activeFrames = scanResult ? scanResult[p.id] : null;
                if (activeFrames && activeFrames.length > 0) {
                  return (
                    <div key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.6)', border: `2px solid ${category.color}`, boxShadow: `0 0 12px ${category.color}40`, color: '#fff', fontWeight: 'bold', fontSize: '13px' }}>
                      <span>{p.name}</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {activeFrames.map(frame => <span key={frame} style={{ backgroundColor: category.color, color: '#000', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '900' }}>[{frame}]</span>)}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={p.id} style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 12px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.02)', border: `1px dashed ${colors.border}`, color: colors.textSub, fontSize: '12px', opacity: scanResult ? 0.3 : 0.8 }}>
                    {p.name}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* BẢNG THÔNG TIN CHI TIẾT 3 LỚP (Mới thêm theo Wireframe) */}
      {detailedFrames && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', borderTop: `1px dashed ${colors.border}`, paddingTop: '20px' }}>
          {renderDetailCard('1D', '🗺️ LỚP 1: KHUNG 1 NGÀY (1D) - LA BÀN VĨ MÔ', detailedFrames['1D'])}
          {renderDetailCard('4H', '🗺️ LỚP 2: KHUNG 4 GIỜ (4H) - BẢN ĐỒ TÁC CHIẾN', detailedFrames['4H'])}
          {renderDetailCard('1H', '🗺️ LỚP 3: KHUNG 1 GIỜ (1H) - ĐIỂM KÍCH HOẠT', detailedFrames['1H'])}
        </div>
      )}

      {/* BẢNG TỔNG HỢP AI */}
      {aiSummary && (
        <div style={{ marginTop: '20px', backgroundColor: 'rgba(59, 130, 246, 0.05)', padding: '16px', borderRadius: '8px', border: `1px solid rgba(59, 130, 246, 0.3)` }}>
          <div style={{ fontSize: '14px', color: '#3b82f6', marginBottom: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🧠 AI TRADER KHUYẾN NGHỊ ({symbol}):
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', color: colors.textSub, lineHeight: '1.5' }}>
            {aiSummary.macro && <div><strong style={{color: colors.textMain}}>👉 TÌNH HÌNH:</strong> {aiSummary.macro} {aiSummary.micro}</div>}
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px dashed ${colors.border}`, color: colors.warn, fontWeight: 'bold' }}>
              ⚡ HÀNH ĐỘNG: <span style={{color: '#fff'}}>{aiSummary.action}</span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
